import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { __stringify, BadImplementationException, exists, filterDuplicates } from '@nu-art/ts-common';
import { LL_H_C, LL_V_L, openContent } from '@nu-art/thunderstorm-frontend/index';
import { PrinterByValue_Boolean, PrinterByValue_Enum, PrinterByValue_Numeric, resolveBase } from '../utils.js';
import { AllDeltaLogic, BaseDeltaLogic, getBaseDeltaOperator, getDeltaTypeOperator, LogicType_AboveNormal, LogicType_BelowNormal, LogicType_Delta, LogicType_DeltaEquals, LogicType_DeltaGreaterEquals, LogicType_DeltaGreaterThan, LogicType_DeltaIn, LogicType_DeltaLesserEquals, LogicType_DeltaLesserThan, LogicType_DeltaPercentage, LogicType_DeltaPercentageEquals, LogicType_DeltaPercentageGreaterEquals, LogicType_DeltaPercentageGreaterThan, LogicType_DeltaPercentageIn, LogicType_DeltaPercentageLesserEquals, LogicType_DeltaPercentageLesserThan, LogicType_Equals, LogicType_Exists, LogicType_NotExists, LogicType_GreaterOrEqual, LogicType_GreaterThan, LogicType_In, LogicType_InNormal, LogicType_IsChiefComplaint, LogicType_InSet, LogicType_LessOrEquals, LogicType_LessThan, LogicType_NotEquals, LogicType_NotInSet, LogicType_Self, NormalRangeOperators, } from '@app/hcs-shared/expression';
import { resolveValueDisplay, valueID_No, valueID_Yes, valueType_SubRange, valueType_TimeRange } from '@app/hcs-shared/core';
import { Renderer_NumericResult } from '../../shared-components/Renderer_NumericResult/Renderer_NumericResult.js';
// Self and exists operators
const prettyPrintTemplate_Self = (_case, leftHand, resolvers) => {
    return _jsxs(_Fragment, { children: ["Value of ", _jsx("i", { children: resolveBase(leftHand, resolvers) })] });
};
const prettyPrintTemplate_Exists = (_case, leftHand, resolvers) => {
    return _jsxs(_Fragment, { children: ["Value of ", _jsx("i", { children: resolveBase(leftHand, resolvers) }), " is available"] });
};
const prettyPrintTemplate_NotExists = (_case, leftHand, resolvers) => {
    return _jsxs(_Fragment, { children: ["Value of ", _jsx("i", { children: resolveBase(leftHand, resolvers) }), " is not available"] });
};
const prettyPrintTemplate_IsChiefComplaint = (_case, leftHand, resolvers) => {
    return _jsxs(_Fragment, { children: [_jsx("i", { children: resolveBase(leftHand, resolvers) }), " is chief complaint"] });
};
// equals
const prettyPrintTemplate_Equals = (_case, leftHand, resolvers) => {
    const base = resolveBase(leftHand, resolvers);
    if (_case.rightHand[0] === valueID_No || _case.rightHand[0] === valueID_Yes) {
        const value = resolvers.resolveValue(_case.rightHand[0]);
        if (!value)
            return `Could not print value, missing value with id ${_case.rightHand[0]}`;
        return _jsx(PrinterByValue_Boolean, { variableName: base, value: value });
    }
    if (typeof _case.rightHand[0] === 'number')
        return _jsx(PrinterByValue_Numeric, { variableName: base, value: _case.rightHand[0] });
    const value = resolvers.resolveValue(_case.rightHand[0]);
    if (!value && _case.rightHand[0])
        return `Could not print value, missing value with id ${_case.rightHand[0]}`;
    return _jsx(PrinterByValue_Enum, { variableName: base, value: value });
};
const prettyPrintTemplate_NotEquals = (_case, leftHand, resolvers) => {
    const base = resolveBase(leftHand, resolvers);
    const value = resolvers.resolveValue(_case.rightHand[0]);
    if (!value)
        return `Could not print value, missing value with id ${_case.rightHand[0]}`;
    return _jsxs(_Fragment, { children: [base, " is not ", _jsx("i", { children: resolveValueDisplay(value) })] });
};
// Numeric operators
const prettyPrintTemplate_NumericRelation = (_case, leftHand, resolvers) => {
    const base = resolveBase(leftHand, resolvers);
    return _jsxs(_Fragment, { children: [base, " ", _jsxs("i", { children: [_case.caseOperator, " ", _case.rightHand[0]] })] });
};
// In set operators
const prettyPrintTemplate_InSet = (_case, leftHand, resolvers) => {
    const base = resolveBase(leftHand, resolvers);
    // Resolve all values to display in set
    const valueDisplays = _case.rightHand.map(id => {
        if (typeof id !== 'string')
            return `Could not print value, incompatible type for id ${id}`;
        const value = resolvers.resolveValue(id);
        if (!value)
            return `Could not print value, missing value with id ${id}`;
        return resolveValueDisplay(value);
    });
    const relation = _case.caseOperator === LogicType_NotInSet ? 'not within' : 'is either';
    return _jsxs(_Fragment, { children: [base, " ", relation, ": ", _jsx("i", { children: valueDisplays.join(', ') })] });
};
// Delta In range
const prettyPrintTemplate_InRange = (_case, leftHand, resolvers) => {
    const base = resolveBase(leftHand, resolvers);
    let range = _case.rightHand;
    if (typeof range[0] === 'string') {
        const value = resolvers.resolveValue(range[0]);
        if (!value)
            return `Could not print value, missing value with id ${range[0]}`;
        if (value.type !== valueType_SubRange && value.type !== valueType_TimeRange)
            return `Could not print value, invalid value type with id ${range[0]}`;
        range = value.data.scope ?? resolveValueDisplay(value);
    }
    return _jsxs(_Fragment, { children: [base, " in ", _jsx("i", { children: Array.isArray(range) ? range.join('-') : range })] });
};
// Delta operators
const prettyPrintTemplate_BaseDelta = (_case, leftHand, resolvers) => {
    const base = resolveBase(leftHand, resolvers);
    const baseDeltaOperator = getBaseDeltaOperator(_case.caseOperator);
    if (!baseDeltaOperator || !BaseDeltaLogic.includes(baseDeltaOperator))
        throw new BadImplementationException('cannot use this template without delta operators');
    return `${baseDeltaOperator === LogicType_Delta ? 'Delta' : 'Delta %'} of ${base}`;
};
const prettyPrintTemplate_ComplexDelta = (_case, leftHand, resolvers) => {
    if (!AllDeltaLogic.includes(_case.caseOperator))
        throw new BadImplementationException('cannot use this template if not complex delta');
    const logicType = getDeltaTypeOperator(_case.caseOperator);
    return _jsxs(_Fragment, { children: [prettyPrintTemplate_BaseDelta(_case, leftHand, resolvers), " is ", logicType, _jsx("i", { children: filterDuplicates(_case.rightHand).join('-') })] });
};
// Normal range operators - FIXME: this might need a refactor
const prettyPrintTemplate_NormalRange = (_case, leftHand, resolvers) => {
    const base = resolveBase(leftHand, resolvers);
    let relation = '';
    let upperScaleFactor = '';
    let lowerScaleFactor = '';
    if (!NormalRangeOperators.includes(_case.caseOperator))
        throw new BadImplementationException(`cannot use operator ${_case.caseOperator} with this template`);
    // get relation rendering
    switch (_case.caseOperator) {
        case LogicType_InNormal:
            relation = 'within normal range';
            break;
        case LogicType_BelowNormal:
            relation = 'below normal range';
            break;
        case LogicType_AboveNormal:
            relation = 'above normal range';
            break;
    }
    if (exists(_case.factor?.lower) && _case.factor?.lower !== 1)
        lowerScaleFactor = `${_case.factor?.lower}x`;
    if (exists(_case.factor?.upper) && _case.factor?.upper !== 1)
        upperScaleFactor = `${_case.factor?.upper}x`;
    return _jsxs(_Fragment, { children: [base, " is ", _jsxs("i", { children: [lowerScaleFactor, " ", upperScaleFactor, " ", relation] })] });
};
const templateMapper = {
    [LogicType_Equals]: prettyPrintTemplate_Equals,
    [LogicType_NotEquals]: prettyPrintTemplate_NotEquals,
    [LogicType_Self]: prettyPrintTemplate_Self,
    [LogicType_Exists]: prettyPrintTemplate_Exists,
    [LogicType_NotExists]: prettyPrintTemplate_NotExists,
    [LogicType_IsChiefComplaint]: prettyPrintTemplate_IsChiefComplaint,
    [LogicType_GreaterOrEqual]: prettyPrintTemplate_NumericRelation,
    [LogicType_GreaterThan]: prettyPrintTemplate_NumericRelation,
    [LogicType_LessOrEquals]: prettyPrintTemplate_NumericRelation,
    [LogicType_LessThan]: prettyPrintTemplate_NumericRelation,
    [LogicType_NotInSet]: prettyPrintTemplate_InSet,
    [LogicType_InSet]: prettyPrintTemplate_InSet,
    [LogicType_In]: prettyPrintTemplate_InRange,
    [LogicType_Delta]: prettyPrintTemplate_BaseDelta,
    [LogicType_DeltaEquals]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaGreaterEquals]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaLesserEquals]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaLesserThan]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaGreaterThan]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaIn]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaPercentage]: prettyPrintTemplate_BaseDelta,
    [LogicType_DeltaPercentageLesserEquals]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaPercentageLesserThan]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaPercentageGreaterThan]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaPercentageGreaterEquals]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaPercentageEquals]: prettyPrintTemplate_ComplexDelta,
    [LogicType_DeltaPercentageIn]: prettyPrintTemplate_ComplexDelta,
    [LogicType_InNormal]: prettyPrintTemplate_NormalRange,
    [LogicType_AboveNormal]: prettyPrintTemplate_NormalRange,
    [LogicType_BelowNormal]: prettyPrintTemplate_NormalRange,
};
function printCase(atom, index, resolvers, status, tooltipResolver) {
    const caseOperator = atom.cases[index].caseOperator;
    if (!caseOperator)
        return 'Missing operator';
    if (!atom.leftHand)
        return 'Missing left hand base';
    if (!atom.cases[index].rightHand)
        return 'Missing value';
    const renderer = templateMapper[caseOperator];
    if (!renderer)
        return `Cannot find renderer for operator ${caseOperator}`;
    return _jsxs(LL_H_C, { className: 'pretty-print_case-container', children: [_jsx("div", { className: 'pretty-print__atom-container content', ...tooltipResolver?.(status, atom.leftHand), children: renderer(atom.cases[index], atom.leftHand, resolvers) }), _jsx(LL_V_L, { style: { marginLeft: 'auto' }, children: exists(atom.cases[index].value) &&
                    _jsx("div", { className: 'pretty-print__atom-container value', children: _jsx("i", { children: atom.cases[index].value[0] === atom.cases[index].value[1]
                                ? atom.cases[index].value[0] : __stringify(atom.cases[index].value) }) }) })] });
}
export const atomPrinter = (atom, resolvers, termStatus, status, tooltipResolver) => {
    const resolveTooltip = (status, atomLeftHand) => {
        if (!atomLeftHand)
            return;
        const varId = atomLeftHand.id;
        if (atomLeftHand.type === 'expression')
            return;
        if (!tooltipResolver)
            return;
        if (!exists(status) || status === null)
            return;
        return openContent.tooltip.top('resolved-content', () => _jsx(_Fragment, { children: tooltipResolver(varId) }), {
            offset: 2,
            xAxisAnchor: 'left'
        });
    };
    if (!atom.cases?.length)
        return 'Missing cases';
    if (atom.cases.length === 1) {
        return printCase(atom, 0, resolvers, status, resolveTooltip);
    }
    const isNumericResult = typeof termStatus === 'number';
    return _jsxs(_Fragment, { children: [_jsxs(LL_H_C, { style: { width: '100%' }, children: [_jsx("div", { className: 'pretty-print__atom-multicase-label', children: "One of:" }), _jsx(Renderer_NumericResult, { numericResult: termStatus, hidden: !(isNumericResult && exists(termStatus)) })] }), _jsxs("ul", { className: 'pretty-print__atom-cases', children: [atom.cases.map((_case, index) => _jsx("li", { children: printCase(atom, index, resolvers, status, resolveTooltip) }, index)), atom.fallbackValue &&
                        _jsxs(LL_H_C, { className: 'pretty-print__atom-container otherwise', children: ["otherwise:", _jsx("i", { children: atom.fallbackValue[0] === atom.fallbackValue[1]
                                        ? atom.fallbackValue[0] : __stringify(atom.fallbackValue) })] })] })] });
};
//# sourceMappingURL=atomPrinter.js.map